import { AxiosResponse } from 'axios';
import { axiosInstance } from '@/api/index';
import { getEmployeesOffersV2 } from '@/api/offers';
import { IOffer } from '@/api/types/offers';
import { IParamsObject, IServerResponse } from '@/api/types';
import {
    ICashbackOfferRaw,
    IContestCashbackGoal,
    IContestCashbackOffer,
    IContestOfferStatus,
    ICreateCashbackOffersPayload,
    IUpdateCashbackOfferPayload,
} from '@/components_v3/affiliate/contest/entities/cashback-offer/model/types';
import { LEAD_CITY_CONTEST_ID } from '@/components_v3/affiliate/contest/shared/config/contest-months';
import { mapCashbackOfferGoals, extractSpecialPeriodFromBonuses } from '@/components_v3/affiliate/contest/shared/lib/map-goals.lib';
import { API_CONTEST_V2_URL } from '@/configs/global';
import { showServerError } from '@/utils';

const SEARCH_OFFERS_ERROR_MESSAGE = 'Не удалось загрузить список офферов для конкурса';

const CASHBACK_OFFERS_BASE = `${API_CONTEST_V2_URL}/v1/employees/lead-city/affiliates`;

/** Только поля для списка конкурса — без fields offers/v2 отдаёт полный оффер и уходит в 504. */
const CONTEST_OFFER_LIST_FIELDS = 'legacy_id,name,goals';

const CONTEST_OFFER_AUTOCOMPLETE_FIELDS = 'legacy_id,name';

/** offers/v2: limit от 5 до 500 (HTTPLimitOffsetTrait). */
const OFFERS_V2_MIN_LIMIT = 5;

function emptyOffersResponse(): IServerResponse<IOffer[]> {
    return {
        data: [],
        meta: { success: true },
        config: {},
        headers: {},
        request: {},
        status: 200,
        statusText: 'OK',
    };
}

function filterOffersBySearch(items: IOffer[], search: string): IOffer[] {
    if (!search) {
        return items;
    }

    const query = search.toLowerCase();

    return items.filter((offer) => {
        const name = offer.name?.toLowerCase() ?? '';
        const legacyId = offer.legacy_id != null ? String(offer.legacy_id) : '';

        return name.includes(query) || legacyId.includes(search);
    });
}

/**
 * Список офферов кешбека партнёра за месяц (contest-service-v2).
 */
export async function getAffiliateCashbackOffers(
    affiliateId: string | number,
    month: string,
): Promise<ICashbackOfferRaw[]> {
    return axiosInstance.request<IServerResponse<ICashbackOfferRaw[]>>({
        method: 'GET',
        url: `${CASHBACK_OFFERS_BASE}/${affiliateId}/cashback-offers`,
        params: { month },
    })
        .then((response: AxiosResponse<IServerResponse<ICashbackOfferRaw[]>>) => response.data.data ?? []);
}

function mapGoals(offer: ICashbackOfferRaw, offerData: IOffer | null): IContestCashbackGoal[] {
    return mapCashbackOfferGoals(offerData, offer.bonuses ?? []);
}

function mapCashbackOffer(
    offer: ICashbackOfferRaw,
    offerDataByLegacyId: Map<number, IOffer>,
): IContestCashbackOffer {
    const offerData = offerDataByLegacyId.get(offer.offer_id) ?? null;
    const bonuses = offer.bonuses ?? [];
    const { enabled: specialPeriodEnabled, period: specialPeriod } = extractSpecialPeriodFromBonuses(bonuses);

    return {
        offerId: offer.offer_id,
        offerName: offerData?.name ?? null,
        customGoal: offer.custom_goal ?? null,
        createdByAffiliate: offer.created_by_affiliate,
        goals: mapGoals(offer, offerData),
        specialPeriodEnabled,
        specialPeriod,
    };
}

async function loadOffersDataByLegacyId(offerIds: number[]): Promise<Map<number, IOffer>> {
    const offerDataByLegacyId = new Map<number, IOffer>();

    if (!offerIds.length) {
        return offerDataByLegacyId;
    }

    try {
        const { data } = await getEmployeesOffersV2({
            legacy_id: offerIds,
            limit: Math.max(offerIds.length, OFFERS_V2_MIN_LIMIT),
            fields: CONTEST_OFFER_LIST_FIELDS,
        });

        for (const offerData of data ?? []) {
            if (offerData.legacy_id != null) {
                offerDataByLegacyId.set(offerData.legacy_id, offerData);
            }
        }
    } catch (err) {
        console.error('loadOffersDataByLegacyId:', err);
        throw err;
    }

    return offerDataByLegacyId;
}

/**
 * Список офферов с названиями и активными целями из offers/v2.
 */
export async function getAffiliateCashbackOffersEnriched(
    affiliateId: string | number,
    month: string,
): Promise<IContestCashbackOffer[]> {
    const offers = await getAffiliateCashbackOffers(affiliateId, month);

    if (!offers.length) {
        return [];
    }

    const offerIds = offers.map((offer) => offer.offer_id);
    const offerDataByLegacyId = await loadOffersDataByLegacyId(offerIds);

    return offers.map((offer) => mapCashbackOffer(offer, offerDataByLegacyId));
}

export async function getContestOfferStatuses(): Promise<IContestOfferStatus[]> {
    return axiosInstance.request<IServerResponse<IContestOfferStatus[]>>({
        method: 'GET',
        url: `${API_CONTEST_V2_URL}/v1/employees/contest/${LEAD_CITY_CONTEST_ID}/offers`,
    })
        .then((response: AxiosResponse<IServerResponse<IContestOfferStatus[]>>) => response.data.data ?? []);
}

export function getContestOfferIdsForMonth(
    offers: IContestOfferStatus[],
    month: string,
): number[] {
    return offers
        .filter((offer) => offer.periods?.some((period) => period.month === month))
        .map((offer) => offer.id);
}

let contestOfferStatusesCache: IContestOfferStatus[] | null = null;

async function loadContestOfferStatuses(): Promise<IContestOfferStatus[]> {
    if (!contestOfferStatusesCache) {
        contestOfferStatusesCache = await getContestOfferStatuses();
    }

    return contestOfferStatusesCache;
}

export function resetContestOfferStatusesCache(): void {
    contestOfferStatusesCache = null;
}

/**
 * Поиск офферов для автокомплита: только активные и участвующие в конкурсе за выбранный месяц.
 */
export async function searchContestOffersForMonth(
    params: IParamsObject & { month?: string },
): Promise<IServerResponse<IOffer[]>> {
    const month = String(params.month ?? '');
    const search = String(params.search ?? '').trim();

    let contestOfferIds: number[];

    try {
        const statuses = await loadContestOfferStatuses();
        contestOfferIds = getContestOfferIdsForMonth(statuses, month);
    } catch (err) {
        console.error('searchContestOffersForMonth: contest offers:', err);
        showServerError(err, SEARCH_OFFERS_ERROR_MESSAGE);
        return emptyOffersResponse();
    }

    if (!contestOfferIds.length) {
        return emptyOffersResponse();
    }

    try {
        const response = await getEmployeesOffersV2({
            legacy_id: contestOfferIds,
            limit: Math.max(contestOfferIds.length, OFFERS_V2_MIN_LIMIT),
            'status[]': 'active',
            fields: CONTEST_OFFER_AUTOCOMPLETE_FIELDS,
        });

        const allowedIds = new Set(contestOfferIds);
        const items = (response.data ?? []).filter(
            (offer) => offer.legacy_id != null && allowedIds.has(offer.legacy_id),
        );

        return {
            ...response,
            data: filterOffersBySearch(items, search),
        };
    } catch (err) {
        console.error('searchContestOffersForMonth: offers v2:', err);
        showServerError(err, SEARCH_OFFERS_ERROR_MESSAGE);
        return emptyOffersResponse();
    }
}

export async function createAffiliateCashbackOffer(
    affiliateId: string | number,
    payload: ICreateCashbackOffersPayload,
): Promise<void> {
    await axiosInstance.request({
        method: 'POST',
        url: `${CASHBACK_OFFERS_BASE}/${affiliateId}/cashback-offers`,
        data: payload,
    });
}

export async function updateAffiliateCashbackOffer(
    affiliateId: string | number,
    offerId: string | number,
    payload: IUpdateCashbackOfferPayload,
): Promise<void> {
    await axiosInstance.request({
        method: 'PATCH',
        url: `${CASHBACK_OFFERS_BASE}/${affiliateId}/cashback-offers/${offerId}`,
        data: {
            month: payload.month,
            ...(payload.custom_goal !== undefined ? { custom_goal: payload.custom_goal } : {}),
            ...(payload.bonuses !== undefined ? { bonuses: payload.bonuses } : {}),
        },
    });
}

export async function deleteAffiliateCashbackOffer(
    affiliateId: string | number,
    offerId: string | number,
    month: string,
): Promise<void> {
    await axiosInstance.request({
        method: 'DELETE',
        url: `${CASHBACK_OFFERS_BASE}/${affiliateId}/cashback-offers/${offerId}`,
        params: { month },
    });
}
